import React from 'react';
import { Link } from 'react-router-dom';
import { CARD } from '../ui';
import { formatMoney, formatMonth } from './format';

const STATE_LABELS = {
  active: { text: 'Billing', style: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' },
  trial: { text: 'Trial', style: 'bg-sky-500/10 text-sky-300 border-sky-500/30' },
  ended: { text: 'Ended', style: 'bg-gray-600/20 text-gray-400 border-gray-600/40' },
  none: { text: 'Not billed', style: 'bg-gray-600/20 text-gray-400 border-gray-600/40' },
};

const stateNote = (sub) => {
  if (!sub) return null;
  if (sub.state === 'trial') return `from ${formatMonth(sub.starts_on)}`;
  if (sub.state === 'ended') return `after ${formatMonth(sub.ends_on)}`;
  if (sub.ends_on) return `until ${formatMonth(sub.ends_on)}`;
  return null;
};

// Every company and the terms it's billed on, with this month's running
// estimate. Where plans are set, and the way into a company's own bill.
const CustomersTable = ({ companies, owedByCompany, onSetPlan }) => (
  <div className={`${CARD} overflow-hidden`}>
    <div className="px-6 py-5 border-b border-gray-700">
      <h2 className="text-lg font-semibold text-white">Customers &amp; plans</h2>
      <p className="text-sm text-gray-500 mt-1">
        A company is only billed once it has a plan. This month's figures are running estimates.
      </p>
    </div>
    {companies.length === 0 ? (
      <p className="px-6 py-10 text-center text-sm text-gray-500">No companies match.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 border-b border-gray-700">
              <th scope="col" className="pl-6 pr-4 py-2 font-medium">Company</th>
              <th scope="col" className="px-4 py-2 font-medium">Plan</th>
              <th scope="col" className="px-4 py-2 font-medium text-right">Seats</th>
              <th scope="col" className="px-4 py-2 font-medium text-right">This month</th>
              <th scope="col" className="px-4 py-2 font-medium text-right">Owed</th>
              <th scope="col" className="px-4 pr-6 py-2"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/60">
            {companies.map((company) => {
              const sub = company.subscription;
              const state = STATE_LABELS[sub?.state || 'none'];
              const owed = owedByCompany.get(company.id) || 0;
              return (
                <tr key={company.id} className="hover:bg-gray-700/20">
                  <td className="pl-6 pr-4 py-3 min-w-[12rem]">
                    <Link
                      to={`/billing/companies/${company.id}`}
                      className="font-medium text-white hover:text-purple-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded"
                    >
                      {company.name}
                    </Link>
                    {company.over_seat_limit && (
                      <span className="block text-xs text-amber-300 mt-0.5">Over its plan's seat limit</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex flex-wrap items-center gap-2">
                      {sub && <span className="text-gray-200">{sub.plan_name}</span>}
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${state.style}`}>{state.text}</span>
                    </span>
                    {(stateNote(sub) || sub?.discount_percent > 0 || sub?.seat_cents != null) && (
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {[
                          stateNote(sub),
                          sub?.discount_percent ? `${sub.discount_percent}% off` : null,
                          sub?.seat_cents != null ? `${formatMoney(sub.seat_cents)}/seat negotiated` : null,
                        ].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-200">
                    {company.seats ?? <span className="text-gray-600">—</span>}
                    {company.pending_seats > 0 && <span className="block text-xs text-gray-500">+{company.pending_seats} invited</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {company.estimate ? (
                      <>
                        <span className={sub?.state === 'active' ? 'text-white' : 'text-gray-500'}>
                          {formatMoney(company.estimate.total_cents)}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {sub?.state === 'active' ? `pace ${formatMoney(company.projected_total_cents)}` : 'not billed'}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${owed ? 'text-white' : 'text-gray-600'}`}>
                    {owed ? formatMoney(owed) : '—'}
                  </td>
                  <td className="px-4 pr-6 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onSetPlan(company)}
                      className="bg-gray-700/50 hover:bg-gray-600/60 text-gray-200 hover:text-white px-2.5 py-1 rounded-md text-xs font-medium border border-gray-600 hover:border-gray-500 whitespace-nowrap"
                    >
                      {sub ? 'Change plan' : 'Set plan'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default CustomersTable;
